/**
 * DatePicker — cleaner, modern replacement for `<input type="date">`.
 *
 * Drop-in contract mirrors the native input:
 *   <DatePicker value={form.date} onChange={(iso)=>set(iso)} disabled placeholder />
 *
 * `value` is always exchanged as ISO `YYYY-MM-DD` so the entire codebase
 * (backend + existing state) keeps working unchanged. If a datetime-local
 * string is passed in we strip the time slice automatically.
 *
 * Design notes (Mint Teal enterprise theme):
 *  · Trigger looks like the standard shadcn Input for visual consistency
 *  · Calendar dropdown opens in a Popover; only DATES are shown — no clocks
 *  · Selected day pill uses `bg-hocp-mint text-hocp-midnight`
 */
import * as React from "react";
import { parseISO, isValid } from "date-fns";
import { Calendar as CalendarIcon, X as XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDateFormat } from "@/lib/dateFormat";

function toISODate(d) {
  if (!d) return "";
  // Local-time formatting — never send the browser's UTC midnight back.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fromISODate(v) {
  if (!v) return undefined;
  // Accept 'YYYY-MM-DD' and 'YYYY-MM-DDTHH:MM' seamlessly.
  const s = String(v).slice(0, 10);
  const d = parseISO(s);
  return isValid(d) ? d : undefined;
}

export function DatePicker({
  value,
  onChange,
  disabled = false,
  placeholder,
  minDate,
  maxDate,
  className,
  triggerClassName,
  clearable = true,
  compact = false,
  "data-testid": testid,
}) {
  const { formatDateWeekday, patternHint } = useDateFormat();
  const selected = fromISODate(value);
  const [open, setOpen] = React.useState(false);
  const ph = placeholder || (compact ? "Date" : `Pick a date (${patternHint})`);
  const label = selected ? (compact ? selected.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : formatDateWeekday(selected)) : ph;

  const handleSelect = (d) => {
    if (onChange) onChange(d ? toISODate(d) : "");
    if (d) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid={testid}
          disabled={disabled}
          className={cn(
            "group inline-flex h-10 w-full items-center gap-2 rounded-lg border border-hocp-border bg-white px-3.5 text-sm text-left transition-colors",
            compact && "h-8 px-2.5 gap-1.5 rounded-md",
            "hover:border-hocp-mint hover:bg-hocp-alice/40",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hocp-mint/40 focus-visible:border-hocp-mint",
            "disabled:cursor-not-allowed disabled:opacity-60",
            "dark:bg-[#0a0a0a] dark:border-[#262626]",
            !selected && "text-hocp-textTertiary",
            triggerClassName,
          )}
        >
          <CalendarIcon className="w-4 h-4 shrink-0 text-hocp-textSecondary group-hover:text-hocp-mint" />
          <span className={cn("flex-1 truncate mono", compact ? "text-[12px]" : "text-[13px]")}>
            {label}
          </span>
          {clearable && selected && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange && onChange(""); }}
              className="opacity-40 hover:opacity-100 transition-opacity"
              aria-label="Clear date"
            >
              <XIcon className="w-3.5 h-3.5" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={cn("w-auto p-0", className)}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected || new Date()}
          onSelect={handleSelect}
          fromDate={fromISODate(minDate)}
          toDate={fromISODate(maxDate)}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
